import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import type { Candidate } from '@axiom/validation';
import {
  Card,
  CardHeader,
  CardSection,
  IconButton,
  Layout,
  LegalTechSkillsDisplay,
  SmallHeader,
  useApi,
} from '@axiom/ui';
import { CandidateProfileIssuesUtils } from '@axiom/utils';

import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateLegalTechSkillsApi } from '../../api/candidate-legal-tech-skills-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';

import { LegalTechSkillsHintBanner } from './LegalTechSkillsHintBanner';
import { LegalTechSkillsEdit } from './LegalTechSkillsEdit';

export const LegalTechSkills: React.FC<{
  candidateId: Candidate['id'];
}> = ({ candidateId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [
    { data: taxonomy },
    { data: legalTechSkills },
    { data: profileIssues },
  ] = useApi(
    TaxonomyApi.readTaxonomy(),
    CandidateLegalTechSkillsApi.readLegalTechSkills(candidateId),
    CandidateProfileIssuesApi.readCandidateProfileIssues(candidateId)
  );

  const [showEditModal, setShowEditModal] = useState(false);
  const openForm = () => {
    setShowEditModal(true);
  };

  useEffect(() => {
    if (searchParams.get('modal') === 'legalTechSkills') {
      openForm();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  return (
    <>
      <Card name="LEGAL_TECH_SKILLS_CARD">
        <CardHeader>
          <Layout position="space-between">
            <SmallHeader>Legal Tech Skills</SmallHeader>
            <IconButton
              name="PENDO_EDIT_LEGAL_TECH_SKILLS_BUTTON"
              icon="pencil"
              pattern="secondary"
              variation="minimal"
              onClick={openForm}
            />
          </Layout>
        </CardHeader>
        <CardSection>
          {CandidateProfileIssuesUtils.hasLegalTechSkillsIssues(
            profileIssues.issues
          ) ? (
            <LegalTechSkillsHintBanner onEditNowClicked={openForm} />
          ) : (
            <LegalTechSkillsDisplay
              legalTechSkills={legalTechSkills}
              taxonomy={taxonomy}
            />
          )}
        </CardSection>
      </Card>
      {!!showEditModal && (
        <LegalTechSkillsEdit
          legalTechSkills={legalTechSkills}
          taxonomy={taxonomy}
          candidateId={candidateId}
          closeModal={() => {
            setShowEditModal(false);
          }}
        />
      )}
    </>
  );
};
